import { formatDateTime } from 'utils/formattedString';
import { Link } from 'react-router-dom';
import { Typography } from 'antd';
import { DynamicObjectType } from 'core/types/common';
import CustomStatusLabel from 'components/commons/CustomStatusLabel';
import { TRANSFER_ORDER_STATUS } from 'constants/constants';
import { upperFirst } from 'lodash';
import i18n from 'i18n';

export const getColumns = (status) => {
  switch (status) {
    case TRANSFER_ORDER_STATUS['AWAITING_CONFIRMATION']?.value:
      return [
        {
          name: 'id',
          title: 'TO ID',
          render: (d: string, record: DynamicObjectType) => (
            <Link to={`/transfer-orders/${record?.id}/show`}>
              <Typography.Paragraph
                ellipsis={{ rows: 1 }}
                style={{ width: 150, color: '#AB2328' }}
              >
                {record?.toId || d}
              </Typography.Paragraph>
            </Link>
          ),
          isDefault: true,
          sorter: true,
        },
        {
          name: 'orderId',
          title: 'Related order ID',
          isDefault: true,
          render: (d: string, record: DynamicObjectType) => (
            <Link
              to={`/${record?.order?.orderType === 'TAB_TRY_BUY' ? 'orders-keep-and-try' : 'order-management'}/${record?.orderId}/show`}
            >
              <Typography.Paragraph
                ellipsis={{ rows: 1 }}
                style={{ width: 150, color: '#AB2328' }}
              >
                {record?.order?.orderNumber}
              </Typography.Paragraph>
            </Link>
          ),
        },
        {
          name: 'createdAt',
          title: 'Created time',
          render: (d: string) => (
            <div style={{ minWidth: 120 }}>{d ? formatDateTime(d) : ''}</div>
          ),
          isDefault: true,
          sorter: true,
        },
        {
          name: 'fromWarehouse',
          title: 'From warehouse',
          render: (d: DynamicObjectType) => (
            <div style={{ minWidth: 120 }}>{d?.name}</div>
          ),
          isDefault: true,
        },
        {
          name: 'toWarehouse',
          title: 'To warehouse',
          render: (d: DynamicObjectType) => (
            <div style={{ minWidth: 120 }}>{d?.name}</div>
          ),
          isDefault: true,
        },
        {
          name: 'itemsCount',
          title: 'Number of items',
          render: (d: number) => <p className="min-w-[150px]">{d}</p>,
          isDefault: true,
          sorter: true,
        },
        {
          name: 'channel',
          title: i18n.t('orders.channel'),
          render: (d: string) => <p>{upperFirst(d)}</p>,
          isDefault: true,
        },
        {
          name: 'description',
          title: i18n.t('orders.note'),
          render: (d: string) => (
            <p className="min-w-[200px]">{upperFirst(d)}</p>
          ),
          isDefault: true,
        },
      ];
    case TRANSFER_ORDER_STATUS['CONFIRMED_PICK']?.value:
      return [
        {
          name: 'id',
          title: 'TO ID',
          render: (d: string, record: DynamicObjectType) => (
            <Link to={`/transfer-orders/${record?.id}/show`}>
              <Typography.Paragraph
                ellipsis={{ rows: 1 }}
                style={{ width: 150, color: '#AB2328' }}
              >
                {record?.toId || d}
              </Typography.Paragraph>
            </Link>
          ),
          isDefault: true,
          sorter: true,
        },
        {
          name: 'orderId',
          title: 'Related order ID',
          isDefault: true,
          render: (d: string, record: DynamicObjectType) => (
            <Link
              to={`/${record?.order?.orderType === 'TAB_TRY_BUY' ? 'orders-keep-and-try' : 'order-management'}/${record?.orderId}/show`}
            >
              <Typography.Paragraph
                ellipsis={{ rows: 1 }}
                style={{ width: 150, color: '#AB2328' }}
              >
                {record?.order?.orderNumber}
              </Typography.Paragraph>
            </Link>
          ),
        },
        {
          name: 'transferNumber',
          title: 'To Number',
          render: (d: string) => <div style={{ minWidth: 120 }}>#{d}</div>,
          isDefault: true,
        },
        {
          name: 'confirmedTime',
          title: 'Confirmed time',
          render: (d: string) => (
            <div style={{ minWidth: 170 }}>{d ? formatDateTime(d) : ''}</div>
          ),
          isDefault: true,
          sorter: true,
        },
        {
          name: 'fromWarehouse',
          title: 'From warehouse',
          render: (d: DynamicObjectType) => (
            <div style={{ minWidth: 120 }}>{d?.name}</div>
          ),
          isDefault: true,
        },
        {
          name: 'toWarehouse',
          title: 'To warehouse',
          render: (d: DynamicObjectType) => (
            <div style={{ minWidth: 120 }}>{d?.name}</div>
          ),
          isDefault: true,
        },
        {
          name: 'itemsCount',
          title: 'Number of items',
          render: (d: number) => <p className="min-w-[150px]">{d}</p>,
          isDefault: true,
          sorter: true,
        },
        {
          name: 'description',
          title: i18n.t('orders.note'),
          render: (d: string) => (
            <p className="min-w-[200px]">{upperFirst(d)}</p>
          ),
          isDefault: true,
        },
      ];
    case TRANSFER_ORDER_STATUS['SHIPPING']?.value:
      return [
        {
          name: 'id',
          title: 'Batch ID',
          render: (d: string) => (
            <Link to={`/transfer-orders/${d}/detail`}>
              <Typography.Paragraph
                ellipsis={{ rows: 1 }}
                style={{ width: 150, color: '#AB2328' }}
              >
                {d}
              </Typography.Paragraph>
            </Link>
          ),
          isDefault: true,
        },
        {
          name: 'deliveredTime',
          title: 'Delivered Time',
          render: (d: string) => (
            <div style={{ minWidth: 120 }}>{d ? formatDateTime(d) : ''}</div>
          ),
          isDefault: true,
          sorter: true,
        },
        {
          name: 'fromWarehouse',
          title: 'From warehouse',
          render: (d, rc: DynamicObjectType) => (
            <div style={{ minWidth: 150 }}>
              {rc?.transferOrders?.[0]?.fromWarehouse?.name}
            </div>
          ),
          isDefault: true,
        },
        {
          name: 'toWarehouse',
          title: 'To warehouse',
          render: (d, rc: DynamicObjectType) => (
            <div style={{ minWidth: 150 }}>
              {rc?.transferOrders?.[0]?.toWarehouse?.name}
            </div>
          ),
          isDefault: true,
        },
        {
          name: 'orderCount',
          title: 'Order Quantity',
          isDefault: true,
          render: (d: number) => <p className="min-w-[200px]">{d || 0}</p>,
          sorter: true,
        },
        {
          name: 'itemsCount',
          title: 'Number of items',
          render: (d: number) => <p className="min-w-[200px]">{d || 0}</p>,
          isDefault: true,
          sorter: true,
        },
        {
          name: 'confirmReceivedTime',
          title: 'Confirm Received Time',
          render: (d: string) => (
            <div style={{ minWidth: 200 }}>{d ? formatDateTime(d) : ''}</div>
          ),
          isDefault: true,
          sorter: true,
        },
      ];
    case TRANSFER_ORDER_STATUS['REJECTED']?.value:
      return [
        {
          name: 'id',
          title: 'TO ID',
          render: (d: string, record: DynamicObjectType) => (
            <Link to={`/transfer-orders/${record?.id}/show`}>
              <Typography.Paragraph
                ellipsis={{ rows: 1 }}
                style={{ width: 150, color: '#AB2328' }}
              >
                {record?.toId || d}
              </Typography.Paragraph>
            </Link>
          ),
          isDefault: true,
          sorter: true,
        },
        {
          name: 'orderId',
          title: 'Related order ID',
          isDefault: true,
          render: (d: string, record: DynamicObjectType) => (
            <Link
              to={`/${record?.order?.orderType === 'TAB_TRY_BUY' ? 'orders-keep-and-try' : 'order-management'}/${record?.orderId}/show`}
            >
              <Typography.Paragraph
                ellipsis={{ rows: 1 }}
                style={{ width: 150, color: '#AB2328' }}
              >
                {record?.order?.orderNumber}
              </Typography.Paragraph>
            </Link>
          ),
        },
        {
          name: 'rejectedTime',
          title: 'Rejected time',
          render: (d: string) => (
            <div style={{ minWidth: 120 }}>{d ? formatDateTime(d) : ''}</div>
          ),
          isDefault: true,
          sorter: true,
        },
        {
          name: 'fromWarehouse',
          title: 'From warehouse',
          render: (d: DynamicObjectType) => (
            <div style={{ minWidth: 120 }}>{d?.name}</div>
          ),
          isDefault: true,
        },
        {
          name: 'toWarehouse',
          title: 'To warehouse',
          render: (d: DynamicObjectType) => (
            <div style={{ minWidth: 120 }}>{d?.name}</div>
          ),
          isDefault: true,
        },
        {
          name: 'itemsCount',
          title: 'Number of items',
          render: (d: number) => <p className="min-w-[150px]">{d}</p>,
          isDefault: true,
          sorter: true,
        },
        {
          name: 'description',
          title: i18n.t('orders.note'),
          render: (d: string) => (
            <p className="min-w-[200px]">{upperFirst(d)}</p>
          ),
          isDefault: true,
        },
      ];
    case TRANSFER_ORDER_STATUS['COMPLETED']?.value:
      return [
        {
          name: 'id',
          title: 'TO ID',
          render: (d: string, record: DynamicObjectType) => (
            <Link to={`/transfer-orders/${record?.id}/show`}>
              <Typography.Paragraph
                ellipsis={{ rows: 1 }}
                style={{ width: 150, color: '#AB2328' }}
              >
                {record?.toId || d}
              </Typography.Paragraph>
            </Link>
          ),
          isDefault: true,
          sorter: true,
        },
        {
          name: 'orderId',
          title: ' ID',
          isDefault: true,
          render: (d: string, record: DynamicObjectType) => (
            <Link to={`/order-management/${record?.orderId}/show`}>
              <Typography.Paragraph
                ellipsis={{ rows: 1 }}
                style={{ width: 150, color: '#AB2328' }}
              >
                {record?.order?.orderNumber}
              </Typography.Paragraph>
            </Link>
          ),
        },
        {
          name: 'transferNumber',
          title: 'TO number',
          isDefault: true,
        },
        {
          name: 'completedTime',
          title: 'Completed time',
          render: (d: string) => (
            <div style={{ minWidth: 120 }}>{d ? formatDateTime(d) : ''}</div>
          ),
          isDefault: true,
          sorter: true,
        },
        {
          name: 'fromWarehouse',
          title: 'From warehouse',
          render: (d: DynamicObjectType) => (
            <div style={{ minWidth: 120 }}>{d?.name}</div>
          ),
          isDefault: true,
        },
        {
          name: 'toWarehouse',
          title: 'To warehouse',
          render: (d: DynamicObjectType) => (
            <div style={{ minWidth: 120 }}>{d?.name}</div>
          ),
          isDefault: true,
        },
        {
          name: 'itemsCount',
          title: 'Number of items',
          render: (d: number) => <p className="min-w-[150px]">{d}</p>,
          isDefault: true,
          sorter: true,
        },
        {
          name: 'description',
          title: i18n.t('orders.note'),
          render: (d: string) => (
            <p className="min-w-[200px]">{upperFirst(d)}</p>
          ),
          isDefault: true,
        },
      ];
    case TRANSFER_ORDER_STATUS['CANCELLED']?.value:
      return [
        {
          name: 'id',
          title: 'TO ID',
          render: (d: string, record: DynamicObjectType) => (
            <Link to={`/transfer-orders/${record?.id}/show`}>
              <Typography.Paragraph
                ellipsis={{ rows: 1 }}
                style={{ width: 150, color: '#AB2328' }}
              >
                {record?.toId || d}
              </Typography.Paragraph>
            </Link>
          ),
          isDefault: true,
          sorter: true,
        },
        {
          name: 'orderId',
          title: 'Related order ID',
          isDefault: true,
          render: (d: string, record: DynamicObjectType) => (
            <Link
              to={`/${record?.order?.orderType === 'TAB_TRY_BUY' ? 'orders-keep-and-try' : 'order-management'}/${record?.orderId}/show`}
            >
              <Typography.Paragraph
                ellipsis={{ rows: 1 }}
                style={{ width: 150, color: '#AB2328' }}
              >
                {record?.order?.orderNumber}
              </Typography.Paragraph>
            </Link>
          ),
        },
        {
          name: 'cancelledTime',
          title: 'Cancelled time',
          render: (d: string) => (
            <div style={{ minWidth: 120 }}>{d ? formatDateTime(d) : ''}</div>
          ),
          isDefault: true,
          sorter: true,
        },
        {
          name: 'fromWarehouse',
          title: 'From warehouse',
          render: (d: DynamicObjectType) => (
            <div style={{ minWidth: 120 }}>{d?.name}</div>
          ),
          isDefault: true,
        },
        {
          name: 'toWarehouse',
          title: 'To warehouse',
          render: (d: DynamicObjectType) => (
            <div style={{ minWidth: 120 }}>{d?.name}</div>
          ),
          isDefault: true,
        },
        {
          name: 'itemsCount',
          title: 'Number of items',
          render: (d: number) => <p className="min-w-[150px]">{d}</p>,
          isDefault: true,
          sorter: true,
        },
        {
          name: 'description',
          title: i18n.t('orders.note'),
          render: (d: string) => (
            <p className="min-w-[200px]">{upperFirst(d)}</p>
          ),
          isDefault: true,
        },
      ];
    default:
      return [
        {
          name: 'id',
          title: 'TO ID',
          render: (d: string, record: DynamicObjectType) => (
            <Link to={`/transfer-orders/${record?.id}/show`}>
              <Typography.Paragraph
                ellipsis={{ rows: 1 }}
                style={{ width: 150, color: '#AB2328' }}
              >
                {record?.toId || d}
              </Typography.Paragraph>
            </Link>
          ),
          isDefault: true,
          sorter: true,
        },
        {
          name: 'orderId',
          title: 'Related order ID',
          render: (d: string, record: DynamicObjectType) => (
            <Link
              to={`/${record?.order?.orderType === 'TAB_TRY_BUY' ? 'orders-keep-and-try' : 'order-management'}/${record?.orderId}/show`}
            >
              <Typography.Paragraph
                ellipsis={{ rows: 1 }}
                style={{ width: 150, color: '#AB2328' }}
              >
                {record?.order?.orderNumber}
              </Typography.Paragraph>
            </Link>
          ),
          isDefault: true,
        },
        {
          name: 'createdAt',
          title: 'Created time',
          render: (d: string) => (
            <div style={{ minWidth: 120 }}>{d ? formatDateTime(d) : ''}</div>
          ),
          isDefault: true,
          sorter: true,
        },
        {
          name: 'fromWarehouse',
          title: 'From warehouse',
          render: (d: DynamicObjectType) => (
            <div style={{ minWidth: 120 }}>{d?.name}</div>
          ),
          isDefault: true,
        },
        {
          name: 'toWarehouse',
          title: 'To warehouse',
          render: (d: DynamicObjectType) => (
            <div style={{ minWidth: 120 }}>{d?.name}</div>
          ),
          isDefault: true,
        },
        {
          name: 'status',
          title: 'status',
          render: (d: string) => (
            <CustomStatusLabel
              dataSource={TRANSFER_ORDER_STATUS}
              record={{ status: d }}
              statusKey="status"
            />
          ),
          isDefault: true,
        },
        {
          name: 'itemsCount',
          title: 'Number of items',
          render: (d: number) => <p className="min-w-[150px]">{d}</p>,
          isDefault: true,
          sorter: true,
        },
        {
          name: 'confirmedTime',
          title: 'Confirmed time',
          render: (d: string) => (
            <div style={{ minWidth: 120 }}>{d ? formatDateTime(d) : ''}</div>
          ),
          sorter: true,
        },
        {
          name: 'deliveredTime',
          title: 'Delivered time',
          render: (d: string) => (
            <div style={{ minWidth: 120 }}>{d ? formatDateTime(d) : ''}</div>
          ),
          sorter: true,
        },
        {
          name: 'transferOrderBatch',
          title: 'Confirm received time',
          render: (d: string, record) => (
            <div style={{ minWidth: 120 }}>
              {record?.confirmReceivedTime
                ? formatDateTime(record?.confirmReceivedTime)
                : ''}
            </div>
          ),
          sorter: true,
        },
        {
          name: 'rejectedTime',
          title: 'Rejected time',
          render: (d: string) => (
            <div style={{ minWidth: 120 }}>{d ? formatDateTime(d) : ''}</div>
          ),
          sorter: true,
        },
        {
          name: 'completedTime',
          title: 'Completed time',
          render: (d: string) => (
            <div style={{ minWidth: 120 }}>{d ? formatDateTime(d) : ''}</div>
          ),
        },
        {
          name: 'cancelledTime',
          title: 'Cancelled time',
          render: (d: string) => (
            <div style={{ minWidth: 120 }}>{d ? formatDateTime(d) : ''}</div>
          ),
          sorter: true,
        },
        {
          name: 'channel',
          title: i18n.t('orders.channel'),
          render: (d: string) => <p>{upperFirst(d)}</p>,
        },
        {
          name: 'description',
          title: i18n.t('orders.note'),
          render: (d: string) => (
            <p className="min-w-[200px]">{upperFirst(d)}</p>
          ),
        },
      ];
  }
};
